import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiRefreshCw, FiShare2, FiX, FiPlay, FiSquare, FiUser, FiInfo, FiZap } from 'react-icons/fi';
import CollectionWizard from './CollectionWizard';
import { AI_ENABLED, IS_STANDALONE } from '../../config/features';
import { monitoringWizardAssist } from '../../services/assistantClient';
import { loadSection } from '../../utils/appSettings';
import { formatApiError } from '../../utils/formatters';
import { orgApi } from '../../services/orgApi';
import './CollectionStatusPanel.css';

const BR = String.fromCharCode(10);
const BR2 = BR + BR;

/**
 * 수집 설정 › 「수집 현황」(2026-10-03) — **내가 볼 수 있는 수집만** 보인다(서버가 거른다: 주인·공유받은 사람·관리자).
 * 주인(또는 관리자)은 여기서 시작·중지하고 조회 공유를 고친다. 공유 상대는 LiveSQL 을 쓴 사람 중에서 찾는다.
 * 권한 규칙의 근거: backend/modules/monitoring/collectionAccess.js
 */
async function readError(res, fallback) {
  try { const j = await res.json(); return j.error || fallback; } catch (_e) { return fallback; }
}

function fmtTime(s) {
  if (!s) return '-';
  return String(s).replace('T', ' ').slice(0, 19);
}

export default function CollectionStatusPanel({ apiBase, labelFor, onMessage, instanceId, onChanged }) {
  const { t, i18n } = useTranslation();
  const [diag, setDiag] = useState(null);                 // { key, status, text }
  // AI 보조 — 칸을 채우는 말만 받는다(등록·시작은 마법사의 단추). AI 제외 빌드에서는 단추가 아예 없다
  const aiAssist = AI_ENABLED ? async (kind, input) => {
    const r = await monitoringWizardAssist({
      kind, input,
      provider: (loadSection('assistant') || {}).provider || undefined,
      lang: (i18n.language || 'ko').startsWith('en') ? 'en' : 'ko',
    });
    const parts = [r.explanation || ''];
    if (Array.isArray(r.statements) && r.statements.length) parts.push(r.statements.join(BR));
    if (Array.isArray(r.steps) && r.steps.length) parts.push(r.steps.map((x, i) => `${i + 1}. ${x}`).join(BR));
    if (Array.isArray(r.rejected) && r.rejected.length) parts.push(t('monitoring:wiz.ai.rejected', { n: r.rejected.length }));
    return { ...r, explanation: parts.filter(Boolean).join(BR2) };
  } : null;
  const diagnose = async (instanceId, g) => {
    const key = `${instanceId}:${g.groupId}`;
    setDiag({ key, status: 'running', text: '' });
    try {
      const r = await aiAssist('diagnose', { failure: { groupId: g.groupId, error: g.lastRun && g.lastRun.error } });
      setDiag({ key, status: 'done', text: r.explanation });
    } catch (err) {
      setDiag({ key, status: 'error', text: formatApiError(err, t) });
    }
  };
  const [rows, setRows] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sharingFor, setSharingFor] = useState(null);     // instanceId
  const [wizardOpen, setWizardOpen] = useState(false);
  const base = `${apiBase}/sql-developer/monitoring`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${base}/collection-status`);
      if (!res.ok) throw new Error(await readError(res, t('monitoring:cstat.loadFailed')));
      const j = await res.json();
      setRows(j.data || []);
      setIsAdmin(!!j.isAdmin);
    } catch (err) {
      setRows([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [base, t]);
  useEffect(() => { load(); }, [load]);

  const act = async (instanceId, groupId, kind) => {
    try {
      const res = await fetch(`${base}/instances/${encodeURIComponent(instanceId)}/groups/${encodeURIComponent(groupId)}/${kind}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (!res.ok) throw new Error(await readError(res, t('monitoring:cstat.actionFailed')));
      onMessage && onMessage('success', t(kind === 'start' ? 'monitoring:toast.started' : 'monitoring:toast.stopped', { name: labelFor(groupId) }));
      load();
    } catch (err) {
      onMessage && onMessage('error', err.message);
    }
  };

  const ownerText = (inst) => {
    if (inst.isMine) return t('monitoring:cstat.ownerMe');
    if (!inst.owner || inst.owner.admin) return t('monitoring:cstat.ownerAdmin');
    return inst.owner.name || inst.owner.usrid;
  };

  return (
    <div className="cstat">
      <div className="cstat-toolbar">
        <button type="button" className="criteria-btn criteria-btn-primary" onClick={() => setWizardOpen(true)}>
          <FiZap size={12} /> {t('monitoring:wiz.open')}
        </button>
        <button type="button" className="criteria-btn criteria-btn-secondary" onClick={load} disabled={loading}>
          <FiRefreshCw size={12} className={loading ? 'cstat-spin' : ''} /> {t('monitoring:cstat.refresh')}
        </button>
        <span className="cstat-note"><FiInfo size={12} /> {t('monitoring:cstat.scopeNote')}</span>
      </div>
      {error && <div className="cstat-error">{error}</div>}
      {/* 수집 설정 창 왼쪽에서 연결을 고르면 그 대상만(2026-10-28) */}
      {rows && rows.filter(r => !instanceId || r.instanceId === instanceId).length === 0 && !error && <div className="cstat-empty">{t('monitoring:cstat.none')}</div>}
      {(rows || []).filter(r => !instanceId || r.instanceId === instanceId).map(inst => (
        <div key={inst.instanceId} className="cstat-card">
          <div className="cstat-card-head">
            <span className="cstat-name">{inst.connName}</span>
            <span className="cstat-target">{inst.instanceId}</span>
            <span className={`cstat-owner ${inst.isMine ? 'mine' : ''}`} title={t('monitoring:cstat.ownerHint')}>
              <FiUser size={11} /> {ownerText(inst)}
            </span>
            {inst.sharedConnection && <span className="cstat-badge">{t('monitoring:cstat.sharedConn')}</span>}
            {inst.publicView && <span className="cstat-badge public">{t('monitoring:cstat.publicView')}</span>}
            {inst.legacy && <span className="cstat-badge" title={t('monitoring:cstat.legacyHint')}>{t('monitoring:cstat.legacy')}</span>}
            <span className="cstat-spacer" />
            {inst.canManage && !IS_STANDALONE && (
              <button type="button" className="criteria-btn criteria-btn-secondary cstat-share-btn"
                onClick={() => setSharingFor(v => (v === inst.instanceId ? null : inst.instanceId))}>
                <FiShare2 size={12} /> {t('monitoring:cstat.share')}{inst.viewers && inst.viewers.length > 0 ? ` (${inst.viewers.length})` : ''}
              </button>
            )}
          </div>
          {sharingFor === inst.instanceId && (
            <SharingEditor
              base={base}
              inst={inst}
              isAdmin={isAdmin}
              onClose={() => setSharingFor(null)}
              onSaved={() => { setSharingFor(null); load(); onMessage && onMessage('success', t('monitoring:cstat.shareSaved')); }}
              onError={(m) => onMessage && onMessage('error', m)}
            />
          )}
          <table className="cstat-table">
            <thead>
              <tr>
                <th>{t('monitoring:cstat.col.group')}</th>
                <th>{t('monitoring:cstat.col.state')}</th>
                <th className="num">{t('monitoring:cstat.col.interval')}</th>
                <th>{t('monitoring:cstat.col.lastRun')}</th>
                <th className="num">{t('monitoring:cstat.col.stored')}</th>
                <th>{t('monitoring:cstat.col.lastStored')}</th>
                {inst.canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {inst.groups.map(g => {
                const failed = g.lastRun && g.lastRun.ok === false;
                const state = g.running ? (failed ? 'error' : 'running') : (g.enabled ? 'pending' : 'stopped');
                return (
                  <tr key={g.groupId}>
                    <td>{labelFor(g.groupId)}</td>
                    <td><span className={`cstat-state ${state}`}>{t(`monitoring:cstat.state.${state}`)}</span></td>
                    <td className="num">{t('monitoring:cstat.everySec', { n: g.intervalSec })}</td>
                    <td className={failed ? 'cstat-fail' : ''} title={failed ? g.lastRun.error : ''}>
                      {g.lastRun ? `${fmtTime(g.lastRun.lastAt)}${failed ? ` · ${t('monitoring:cstat.failed')}` : g.lastRun.rows != null ? ` · ${t('monitoring:cstat.rows', { n: g.lastRun.rows })}` : ''}` : '-'}
                      {failed && aiAssist && inst.canManage && (
                        <button type="button" className="cstat-ai" title={t('monitoring:wiz.ai.diagnose')}
                          disabled={diag && diag.status === 'running'} onClick={() => diagnose(inst.instanceId, g)}>
                          <FiZap size={11} />
                        </button>
                      )}
                    </td>
                    <td className="num">{Number(g.storedRows || 0).toLocaleString()}</td>
                    <td>{fmtTime(g.lastStoredAt)}</td>
                    {inst.canManage && (
                      <td className="cstat-actions">
                        {g.running
                          ? <button type="button" className="criteria-btn-small criteria-btn-secondary" onClick={() => act(inst.instanceId, g.groupId, 'stop')}><FiSquare size={11} /> {t('monitoring:cstat.stop')}</button>
                          : <button type="button" className="criteria-btn-small criteria-btn-primary" onClick={() => act(inst.instanceId, g.groupId, 'start')}><FiPlay size={11} /> {t('monitoring:cstat.start')}</button>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      {diag && (
        <div className={`cstat-diag ${diag.status}`}>
          <div className="cstat-diag-head">
            <FiZap size={12} /> {t('monitoring:wiz.ai.diagnoseTitle', { name: labelFor(diag.key.split(':').slice(1).join(':')) })}
            <button type="button" aria-label={t('common:ui.close')} onClick={() => setDiag(null)}><FiX size={12} /></button>
          </div>
          <div className="cstat-diag-body">{diag.status === 'running' ? t('monitoring:wiz.ai.thinking') : diag.text}</div>
        </div>
      )}
      <CollectionWizard open={wizardOpen} onClose={() => setWizardOpen(false)} apiBase={`${apiBase}/sql-developer`} onDone={() => { load(); if (onChanged) onChanged(); }} aiAssist={aiAssist} />
    </div>
  );
}

/** 조회 공유 편집 — 사용자 찾기(두 글자 이상)·추가·빼기, 관리자는 공용 연결 수집을 전체 공개 */
function SharingEditor({ base, inst, isAdmin, onClose, onSaved, onError }) {
  const { t } = useTranslation();
  const [viewers, setViewers] = useState(inst.viewers || []);
  const [publicView, setPublicView] = useState(!!inst.publicView);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);
  // 조직 전체에 공유(2026-10-03) — 내가 든 조직만 고를 수 있다(서버도 같은 판정)
  const [myOrgs, setMyOrgs] = useState([]);
  useEffect(() => {
    if (IS_STANDALONE) return;
    orgApi(base.replace(/\/monitoring$/, '')).mine().then(l => setMyOrgs(l.filter(o => o.status === 'active'))).catch(() => setMyOrgs([]));
  }, [base]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setFound([]); return undefined; }
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${base}/users/search?q=${encodeURIComponent(term)}`);
        const j = res.ok ? await res.json() : { data: [] };
        setFound((j.data || []).filter(u => !viewers.some(v => v.usrid === u.usrid) && u.usrid !== (inst.owner && inst.owner.usrid)));
      } catch (_e) { setFound([]); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, base, viewers, inst.owner]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { viewers: viewers.map(v => v.usrid) };
      if (isAdmin && inst.sharedConnection) body.publicView = publicView;
      const res = await fetch(`${base}/instances/${encodeURIComponent(inst.instanceId)}/sharing`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(await readError(res, t('monitoring:cstat.shareFailed')));
      onSaved();
    } catch (err) {
      onError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cstat-share">
      <div className="cstat-share-hint">{t('monitoring:cstat.shareHint')}</div>
      <div className="cstat-chips">
        {viewers.length === 0 && <span className="cstat-muted">{t('monitoring:cstat.noViewers')}</span>}
        {viewers.map(v => (
          <span key={v.usrid} className="cstat-chip" title={v.login || v.usrid}>
            {v.org ? t('common:org.chip', { name: v.org.name }) : (v.name || v.usrid)}
            <button type="button" aria-label={t('monitoring:cstat.removeViewer')} onClick={() => setViewers(vs => vs.filter(x => x.usrid !== v.usrid))}><FiX size={10} /></button>
          </span>
        ))}
      </div>
      <input
        type="text"
        className="criteria-text-input cstat-search"
        placeholder={t('monitoring:cstat.searchPlaceholder')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {found.length > 0 && (
        <div className="cstat-found">
          {found.map(u => (
            <button key={u.usrid} type="button" className="cstat-found-item" onClick={() => { setViewers(vs => [...vs, u]); setQ(''); setFound([]); }}>
              <b>{u.name}</b> <span className="cstat-muted">{u.login}</span>
            </button>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && found.length === 0 && <div className="cstat-muted">{t('monitoring:cstat.noMatch')}</div>}
      {myOrgs.filter(o => !viewers.some(v => v.usrid === `org:${o.id}`)).length > 0 && (
        <div className="cstat-orgs">
          <span className="cstat-muted">{t('monitoring:cstat.shareOrg')}</span>
          {myOrgs.filter(o => !viewers.some(v => v.usrid === `org:${o.id}`)).map(o => (
            <button key={o.id} type="button" className="criteria-btn-small criteria-btn-secondary" onClick={() => setViewers(vs => [...vs, { usrid: `org:${o.id}`, org: { id: o.id, name: o.name } }])}>
              + {o.name}
            </button>
          ))}
        </div>
      )}
      {isAdmin && inst.sharedConnection && (
        <label className="cstat-public">
          <input type="checkbox" checked={publicView} onChange={(e) => setPublicView(e.target.checked)} />
          {t('monitoring:cstat.publicToggle')}
        </label>
      )}
      <div className="cstat-share-actions">
        <button type="button" className="criteria-btn criteria-btn-secondary" onClick={onClose}>{t('monitoring:act.cancel')}</button>
        <button type="button" className="criteria-btn criteria-btn-primary" onClick={save} disabled={saving}>{t('monitoring:cstat.saveShare')}</button>
      </div>
    </div>
  );
}
